import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { refreshProductTypes } from "@/lib/productTypes";
import { getDeviceId, getDeviceLabel } from "@/lib/device";
import { setNewProductDays } from "@/lib/productBadges";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // 2026-07 · Prime the R2 public URL so photoThumb() rewrites <img src> to
    // the direct CDN link (bypasses our K8s ingress and lets browsers cache
    // the binary properly). Fire-and-forget; if it fails photoThumb falls
    // back to /api/uploads/… which still works.
    (async () => {
      try {
        const { data } = await api.get("/settings/public");
        if (data?.r2_public_url) {
          window.__quotesys_r2_public_url__ = data.r2_public_url;
        }
        // 2026-10-11 · Dual-bucket. Survey photos live on a separate R2 bucket
        // with (optionally) its own public CDN URL. photoThumb/photoFull read
        // this when the key starts with "survey-" so we don't rewrite survey
        // URLs onto the primary bucket's CDN and 404.
        if (data?.r2_market_public_url !== undefined) {
          window.__quotesys_r2_market_public_url__ = data.r2_market_public_url || "";
        }
        if (data?.new_product_days) setNewProductDays(data.new_product_days);
      } catch { /* offline — stay on backend-relative URLs */ }
      // Load the dynamic product-type dictionary (2026-07). Cached in a
      // window global so ProductFilters / ProductForm can read synchronously.
      refreshProductTypes();
    })();
  }, [checkAuth]);

  const value = useMemo(() => ({
    user,
    checkAuth,
    login: async (email, password) => {
      try {
        const { data } = await api.post("/auth/login", {
          email,
          password,
          device_id: getDeviceId(),
          device_label: getDeviceLabel(),
        });
        setUser(data);
        return { success: true };
      } catch (e) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
    logout: async () => {
      try {
        await api.post("/auth/logout");
      } catch (error) {
        console.warn("Logout request failed, clearing session locally:", error);
      }
      setUser(false);
    },
  }), [user, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
